/** Criar a conta da empresa (SPEC-IDN-001).
 *
 * O cadastro cria tenant, administrador, papel e funil padrão numa transação
 * só, e **já devolve a sessão** — por isso não há passo de "agora entre com
 * sua senha". Pedir login logo depois de escolher a senha seria uma parede no
 * exato momento em que a pessoa entra no produto pela primeira vez, o mesmo
 * raciocínio já aplicado ao aceite de convite.
 *
 * A validação de senha mora no servidor (comprimento e vazamento em HIBP). Aqui
 * o comprimento é conferido antes de enviar, para não gastar uma ida à rede no
 * erro mais comum — mas o veredito continua sendo o do servidor, e o `422` por
 * senha vazada é exibido no campo, não como erro geral do formulário.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError, FalhaDeRede } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { FunilDeAmostra, PortalDeAcesso } from "@/shared/ui/PortalDeAcesso";

import { useSessao } from "./SessaoProvider";

const SENHA_MINIMA = 12;

export function TelaDeCadastro({ aoVoltar }: { aoVoltar: () => void }) {
  const { cadastrar } = useSessao();
  const [empresa, setEmpresa] = useState("");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [errosDeCampo, setErrosDeCampo] = useState<Record<string, string>>({});

  const senhaCurta = senha.length > 0 && senha.length < SENHA_MINIMA;
  const podeEnviar =
    empresa.trim().length >= 2 &&
    nome.trim().length >= 2 &&
    email.trim().length > 0 &&
    senha.length >= SENHA_MINIMA;

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (!podeEnviar || enviando) return;

    setEnviando(true);
    setErro(null);
    setErrosDeCampo({});

    try {
      await cadastrar({
        company_name: empresa.trim(),
        full_name: nome.trim(),
        email: email.trim(),
        password: senha,
      });
      // Sem navegação aqui: o provedor passa a sessão para "autenticado" e o
      // App troca de tela sozinho. Empurrar uma rota daqui competiria com ele.
    } catch (falha) {
      if (falha instanceof ApiError) {
        // O servidor aponta o campo culpado em `errors` (RFC 9457). Mostrar a
        // mensagem embaixo do campo é o que permite corrigir sem adivinhar
        // qual dos quatro está errado.
        const porCampo: Record<string, string> = {};
        for (const invalido of falha.camposInvalidos) {
          porCampo[invalido.field] = invalido.message;
        }
        setErrosDeCampo(porCampo);

        if (Object.keys(porCampo).length === 0) {
          setErro(
            falha.status === 429
              ? "Muitas tentativas de cadastro deste local. Aguarde alguns minutos."
              : falha.message,
          );
        }
      } else if (falha instanceof FalhaDeRede) {
        setErro("Não conseguimos falar com o servidor. Verifique sua conexão.");
      } else {
        setErro("Algo deu errado. Tente de novo.");
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PortalDeAcesso painel={<OQueVoceRecebe />}>
      <form
        onSubmit={(evento) => void enviar(evento)}
        noValidate
        aria-label="Criar a conta da empresa"
        className="flex w-full flex-col gap-5"
      >
        <header className="flex flex-col gap-1">
          <h1 className="font-display text-display-md text-texto">Criar conta</h1>
          <p className="font-texto text-body text-texto-suave">
            Você será o administrador da empresa. Leva menos de um minuto.
          </p>
        </header>

        {erro !== null && (
          <div
            role="alert"
            className="rounded-md border border-carmim/40 bg-carmim/10 px-4 py-3 font-texto text-body text-texto"
          >
            {erro}
          </div>
        )}

        <Campo
          rotulo="Nome da empresa"
          name="empresa"
          autoComplete="organization"
          required
          autoFocus
          value={empresa}
          erro={errosDeCampo.company_name}
          onChange={(evento) => {
            setEmpresa(evento.target.value);
          }}
          placeholder="Acme Ltda"
        />

        <Campo
          rotulo="Seu nome"
          name="nome"
          autoComplete="name"
          required
          value={nome}
          erro={errosDeCampo.full_name}
          onChange={(evento) => {
            setNome(evento.target.value);
          }}
          placeholder="Juliana Souza"
        />

        <Campo
          rotulo="Seu e-mail"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          erro={errosDeCampo.email}
          onChange={(evento) => {
            setEmail(evento.target.value);
          }}
          placeholder="voce@empresa.com.br"
        />

        <Campo
          rotulo="Senha"
          type="password"
          name="senha"
          autoComplete="new-password"
          required
          value={senha}
          erro={errosDeCampo.password ?? (senhaCurta ? senhaCurtaAviso() : undefined)}
          ajuda={`Ao menos ${SENHA_MINIMA} caracteres. Evite uma senha que você já use em outro lugar.`}
          onChange={(evento) => {
            setSenha(evento.target.value);
          }}
        />

        <Botao type="submit" carregando={enviando} disabled={!podeEnviar} larguraTotal tamanho="lg">
          Criar conta
        </Botao>

        <p className="text-center font-texto text-caption text-texto-suave">
          Já tem conta?{" "}
          <button
            type="button"
            onClick={aoVoltar}
            className="rounded-sm font-medium text-acao underline underline-offset-2 hover:text-acao-viva focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
          >
            Entrar
          </button>
        </p>
      </form>
    </PortalDeAcesso>
  );
}

/** O painel de quem está decidindo.
 *
 * Quem chega aqui já sabe o que é o produto — veio da tela de entrada. O que
 * pesa agora é o que acontece ao clicar, e a resposta honesta é concreta: sai
 * daqui com o funil montado e pode arrastar o primeiro negócio na hora.
 *
 * As etapas são as de verdade, com as cores de verdade, no mesmo vocabulário
 * do cabeçalho de coluna do board. Ilustração genérica prometeria uma coisa e
 * entregaria outra; esta promete exatamente o que a próxima tela mostra.
 */
function OQueVoceRecebe() {
  return (
    <div className="flex max-w-[440px] flex-col gap-6">
      <p className="font-display text-display-lg leading-tight text-acao-contraste">
        Seu funil já vem montado.
      </p>
      <p className="font-texto text-body-lg text-acao-contraste/85">
        Cinco etapas prontas para o primeiro negócio. Renomeie, reordene ou crie as suas depois.
      </p>

      <FunilDeAmostra />

      <p className="font-texto text-caption text-acao-contraste/75">
        Você entra como administrador e já pode convidar o time. Nada de configuração antes de
        começar a usar.
      </p>
    </div>
  );
}

function senhaCurtaAviso(): string {
  return `A senha precisa de ao menos ${SENHA_MINIMA} caracteres.`;
}
